/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    SessionCreateDeviceTypeEnum,
    SessionCreateDrmTypeEnum,
    SessionCreateTransportEncodingTypeEnum,
} from 'client-code/session-manager';

import { DeviceModel } from '../../device';
import { ErrorTypes } from '../../error';
import { Log, VuLogger } from '../../logging';
import { RetryMechanism } from '../../retrymechanism/RetryMechanism';
import { RetryOptions } from '../../retrymechanism/RetryOptions';
import { TSession } from '../../types';
import { TCreateSessionParams } from '../../types/TCreateSessionParams';
import { UniqueIdentifier } from '../../utils';
import { StreamingSessionImpl } from './StreamingSessionImpl';
import { SessionState } from '../enums';
import { ISessionCreateModel } from '../interfaces';

export class SocuStreamingSessionImpl extends StreamingSessionImpl {
    TAG: string = 'SocuStreamingSessionImpl';

    private static oISocuStreamingSession: SocuStreamingSessionImpl;

    public static getInstance(): SocuStreamingSessionImpl {
        if (!SocuStreamingSessionImpl.oISocuStreamingSession)
            SocuStreamingSessionImpl.oISocuStreamingSession = new SocuStreamingSessionImpl();
        return SocuStreamingSessionImpl.oISocuStreamingSession;
    }

    public createStreamingSession(sessionCreateModel: ISessionCreateModel): Promise<void> {
        const { channel, transportEncodingType, watchAction } = sessionCreateModel;
        if (!channel) return Promise.resolve();
        const { partnerStationId } = channel;
        const { partnerRootOfferId, partnerAssetId, partnerId } = watchAction?.kernel || {};

        return new Promise((resolve, reject) => {
            if (!partnerId && !partnerRootOfferId && !partnerAssetId) {
                this.mError = this.getPlaybackError(
                    ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904,
                );
                this.mSessionState = SessionState.ERROR;
                return reject();
            }
            // Need to update devicetype, drmtype, transportEncodingType based on actual data
            const sessionParams: TCreateSessionParams = {
                deviceType: SessionCreateDeviceTypeEnum.IptvSmartTv,
                drmType: SessionCreateDrmTypeEnum.Widevine,
                partnerId: partnerId || '',
                partnerServicesCustomerId: DeviceModel.getTiVoSettings().getPartnerCustomerId(),
                partnerStationId: partnerStationId || '',
                transportEncodingType: <SessionCreateTransportEncodingTypeEnum>(
                    transportEncodingType
                ),
                partnerRootOfferId: partnerRootOfferId || '',
                partnerAssetId: partnerAssetId || '',
            };

            const createSession = this.mServices.createSession.bind(this.mServices);
            // TODO: need to check if retry is needed.
            RetryMechanism.callServiceWithRetry<TSession>(
                createSession,
                [sessionParams, UniqueIdentifier.getNextSessionNetworkCallId()],
                <RetryOptions>this.SESSION_MANAGER_DEFAULT_RETRY_OPTIONS,
            )
                .then((response: TSession) => {
                    if (
                        response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()
                    ) {
                        this.oSession = response.session;
                        VuLogger.setSessionId(this.oSession.sessionId);
                        this.mSessionState = SessionState.SUCCESS;
                        resolve();
                    }
                })
                .catch((error: any) => {
                    // service is unreachable
                    if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                        this.mError = this.getSessionError(error);
                        Diagnostics.error(this.TAG, `{createStreamingSession: ${error}}`);
                        Log.e(this.TAG, `{createStreamingSession: ${error}}`);
                        this.mSessionState = SessionState.ERROR;
                        reject();
                    }
                });
        });
    }
}
